export type ShopSyncProgressStatus = 'RUNNING';

export interface ProductSyncState {
  processedCount?: number;
  shopId?: number | string;
  shopName?: string;
  stage?: string;
  status?: string;
  syncType?: string;
  totalCount?: number;
}

export interface ShopSyncProgress {
  percentage: number;
  shopId: number | string;
  shopName?: string;
  status: ShopSyncProgressStatus;
}

const ACTIVE_STATUSES = new Set(['PENDING', 'RUNNING']);

function overallState(states: ProductSyncState[]) {
  return states.find((item) => item.syncType === 'ALL');
}

function activePercentage(states: ProductSyncState[]) {
  const measurable = states.filter((item) => Number(item.totalCount || 0) > 0);
  if (measurable.length > 0) {
    const total = measurable.reduce(
      (sum, item) => sum + Number(item.totalCount || 0),
      0,
    );
    const processed = measurable.reduce(
      (sum, item) =>
        sum +
        Math.min(
          Number(item.processedCount || 0),
          Number(item.totalCount || 0),
        ),
      0,
    );
    return Math.max(1, Math.min(99, Math.round((processed / total) * 100)));
  }
  return states.some((item) => item.status === 'RUNNING') ? 10 : 3;
}

export function aggregateProductSyncByShop(
  states: ProductSyncState[],
): ShopSyncProgress[] {
  const grouped = new Map<number | string, ProductSyncState[]>();
  states
    .filter((item) => item.syncType !== 'SHOP_LEASE')
    .forEach((item) => {
      const shopId = item.shopId ?? item.shopName;
      if (shopId === undefined) return;
      const group = grouped.get(shopId) || [];
      group.push(item);
      grouped.set(shopId, group);
    });

  return [...grouped.entries()].flatMap(([shopId, group]) => {
    const active = group.filter((item) =>
      ACTIVE_STATUSES.has(item.status || ''),
    );
    if (active.length > 0) {
      const activeOverall = overallState(active);
      return [
        {
          percentage: activePercentage(
            activeOverall ? [activeOverall] : active,
          ),
          shopId,
          shopName: group.find((item) => item.shopName)?.shopName,
          status: 'RUNNING' as const,
        },
      ];
    }
    return [];
  });
}
